// ============================================================
// attack/miss - a missed roll: the re-roll prompt (Phase 6), or the miss
// written - and a missed blast detonated where it scattered.
// ============================================================
import { areaOf } from '../../engine/areaShapes';
import { rollScatter, scatterNote as describeScatter, type ScatterResult } from '../../engine/scatter';
import { rollDie, getWSP, calcDoF } from '../../engine/index';
import { OVERHEAT_DMG, hitFrom, targetNameOf, type Swing } from './armed';
import type { AttackContext } from './context';
import { writeMissNarrative } from './outcome';
import { resumeAttackFromHit } from './shot';

/** Phase 6: who may re-roll this miss - Blademaster, Sure Strike, a stance, or a Doomed target (v8.368.5). */
function rerollTalent(ctx: AttackContext, sw: Swing): string | null {
  const stats = ctx.attackerCharStats;
  if (sw.isMelee && stats?.hasBlademaster) return 'Blademaster';
  if (sw.isMelee && ctx.isCalledShot && stats?.hasSureStrike) return 'Sure Strike';
  // A stance that grants a re-roll of this attack (engine/character/situational): named by the stance.
  if (stats?.situationalReroll) return stats.situationalReroll;
  return sw.onHit.luck > 0 ? 'Doomed target' : null;
}

/** PAUSE on a re-rollable miss: the UI shows the prompt. Returns whether it did. */
function offerReroll(ctx: AttackContext, sw: Swing, attackRoll: number, dof: number): boolean {
  const talentName = rerollTalent(ctx, sw);
  if (!talentName) return false;
  ctx.setPendingReroll({
    talentName,
    originalRoll: attackRoll,
    modifiedSkill: sw.modifiedSkill,
    slot: sw.slot,
    isMelee: sw.isMelee,
    beatKind: sw.beatKind,
    specialNotes: [...sw.notes],
    overheatDmg: OVERHEAT_DMG,
    cdResult: sw.cdResult,
    attackMod: sw.attackMod,
    tabId: ctx.activeTab,
    dof,
    targetName: targetNameOf(sw.tab),
    woundsAfter: sw.tab.target.wounds.current,
    woundsMax: sw.tab.target.wounds.max,
    skillBreakdown: sw.skillBreakdown,
    actorId: ctx.actorId ?? null,
    allOut: !!ctx.isAllOutAttack,
  });
  ctx.historyTrack.current = true;
  return true;
}

/**
 * A MISSED BLAST STILL LANDS SOMEWHERE - the block's rider (v8.372.4).
 *
 * Not melee: a swing that misses simply misses, which is also what keeps a
 * melee blast from detonating on the man swinging it (user, 2026-08-17).
 * Not with the ruling switched off either. Guided/Erratic (v8.291.0) shift
 * the metres AFTER the roll, so the dice sequence is untouched.
 */
function missedBlastScatter(ctx: AttackContext, sw: Swing, dof: number): ScatterResult | null {
  if (!(areaOf(sw.slot).scatterOnMiss && !sw.isMelee && ctx.blastScatter)) return null;
  let scatter = rollScatter(dof, rollDie);
  const drift = getWSP(sw.sp, 'Erratic') - getWSP(sw.sp, 'Guided');
  if (drift !== 0) {
    const adjusted = Math.max(0, scatter.metres + drift);
    sw.notes.push((drift < 0 ? 'Guided' : 'Erratic') + ': scatter ' + scatter.metres + 'm → ' + adjusted + 'm');
    scatter = { ...scatter, metres: adjusted };
  }
  sw.notes.push(describeScatter(scatter));
  return scatter;
}

/**
 * A miss: the re-roll prompt, or the miss written - and a missed blast
 * detonated where it scattered. Returns true when it is WAITING on the
 * prompt: the attack is not over, and whoever settles it does so later.
 */
export function missSwing(ctx: AttackContext, sw: Swing, attackRoll: number): boolean {
  const dof = calcDoF(attackRoll, sw.modifiedSkill);
  if (offerReroll(ctx, sw, attackRoll, dof)) return true;
  // A melee miss still TOUCHES an exposed target (v8.353.0) - a spined hide
  // bites back at a whiff too. The re-roll paths do not report.
  if (sw.isMelee) ctx.onTouched?.({ target: sw.tab.target, outcome: 'miss', isMelee: sw.isMelee });
  const scatter = missedBlastScatter(ctx, sw, dof);
  writeMissNarrative(ctx, {
    tabId: ctx.activeTab,
    attackRoll,
    modifiedSkill: sw.modifiedSkill,
    dof,
    slot: sw.slot,
    notes: sw.notes,
    overheatDmg: OVERHEAT_DMG,
    cdResult: sw.cdResult,
    targetName: targetNameOf(sw.tab),
    woundsAfter: sw.tab.target.wounds.current,
    woundsMax: sw.tab.target.wounds.max,
    beatKind: sw.beatKind,
  });
  // The overheat and the countdown were spent by the miss; the failed roll
  // goes through as it is, so the detonation gets no degrees of success -
  // minimum effect, which is what a mishap should be.
  if (!scatter) return false;
  const detonate = () => resumeAttackFromHit(ctx, { ...hitFrom(ctx, sw, attackRoll), cdResult: null, scatter });
  // With no map to ask, who it catches is ruled by hand: the caller resumes when that is in.
  if (ctx.parkScatter) ctx.parkScatter({ scatter, slot: sw.slot, tabId: ctx.activeTab }, detonate);
  else detonate();
  return false;
}
